import { Link, useParams } from 'react-router-dom'
import { api } from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { useSeason } from './useSeason'
import { SeasonSwitcher } from './SeasonSwitcher'
import { EventLine } from './Schedule'
import { usePlayerPaths } from '../../components/playerPaths'

/**
 * The season standings (FR-038).
 *
 * # These are the COMMITTED standings, and the view says so
 *
 * They move exactly once per league event, at Complete Round (FR-060, SC-022).
 * An event in progress does not move them — the live projection is a separate
 * route and a separate view, because a projected standing must never be
 * presentable as a settled one (FR-063, SC-023). Saying which one this is, on
 * the page, is the last line of that defence.
 */
export function Standings() {
  const { pathFor } = usePlayerPaths()
  const { seasonId } = useParams()
  const season = useSeason(seasonId)
  const standings = useApi(
    (s) =>
      season.state === 'ready' ? api.standings(season.data.id, s) : new Promise<never>(() => {}),
    [season.state === 'ready' ? season.data.id : 0],
  )

  if (season.state === 'loading') return <Loading what="the season" />
  if (season.state === 'error') return <Failed what="The season" error={season.error} />
  if (standings.state === 'loading') return <Loading what="the standings" />
  if (standings.state === 'error') return <Failed what="The standings" error={standings.error} />

  const {
    rows,
    remaining_schedule,
    committed_through_event_id,
    official_points_source,
    counting_rounds,
    scheduled_rounds,
    has_matches,
    champion,
  } = standings.data
  // **A halved match is shown only in a season that has one.** This league
  // settles a level match on total net and then the hardest hole, so a halve
  // exists only where the commissioner records one -- and a column reading 0
  // for everybody, headed by a bare "H", was a question rather than a fact.
  const has_halves = rows.some((row) => row.matches_halved > 0)

  return (
    <section>
      <h2>{season.data.name} standings</h2>
      <SeasonSwitcher current={season.data.id} view="standings" />
      <p className="note">
        Committed through{' '}
        {committed_through_event_id ? `event ${committed_through_event_id}` : 'no event yet'}. These
        change once per night, when the round is completed — not while one is being played.
      </p>
      {counting_rounds !== undefined && (
        <p className="note">
          Each player's best {counting_rounds} rounds
          {scheduled_rounds ? ` of ${scheduled_rounds}` : ''} count toward their total. A round left
          out still stands — it keeps its points and its scorecard, it simply is not among the best{' '}
          {counting_rounds}.
        </p>
      )}

      {champion && (
        /* A finished season says who won it, above the table -- and a playoff
           champion need not be first in it, which is why this is not simply the
           top row. */
        <p className="champion-banner" data-testid="champion">
          <span className="champion-label">{season.data.name} champion</span>{' '}
          <Link to={`${pathFor(champion.player_id)}/seasons/${season.data.id}`}>
            {champion.display_name}
          </Link>
          <span className="champion-how">
            {champion.decided_by === 'playoff' && champion.final
              ? ` — won the final against ${champion.final.opponent_name}${
                  champion.final.margin ? `, ${champion.final.margin}` : ''
                }`
              : champion.decided_by === 'playoff'
                ? ' — won the playoff'
                : ` — ${champion.points} points`}
          </span>
        </p>
      )}

      <div className="split">
        <section>
          {rows.length === 0 ? (
            <Empty>No event has been completed yet, so nobody has any points.</Empty>
          ) : (
            <table className="table standings-table table-cards">
              <caption className="visually-hidden">
                Season standings by {official_points_source}
              </caption>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Player</th>
                  {/* A stroke-play season plays no matches: 0-0-0 for everyone. */}
                  {has_matches && (
                    <>
                      <th scope="col" className="numeric">
                        W
                      </th>
                      <th scope="col" className="numeric">
                        L
                      </th>
                      {has_halves && (
                        <th scope="col" className="numeric">
                          H
                        </th>
                      )}
                    </>
                  )}
                  <th scope="col" className="numeric">
                    Points
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.player_id}>
                    <td className="numeric">{row.position}</td>
                    <th scope="row">
                      <Link to={`${pathFor(row.player_id)}/seasons/${season.data.id}`}>
                        {row.display_name}
                      </Link>
                      {champion?.player_id === row.player_id && (
                        <span
                          className="trophy"
                          role="img"
                          aria-label={`${season.data.name} champion`}
                          title={`${season.data.name} champion`}
                        >
                          🏆
                        </span>
                      )}
                    </th>
                    {has_matches && (
                      <>
                        <td className="numeric">{row.matches_won}</td>
                        <td className="numeric">{row.matches_lost}</td>
                        {has_halves && <td className="numeric">{row.matches_halved}</td>}
                      </>
                    )}
                    {/* A decimal string, shown as it arrived: ties split an award,
                    which is where 512.5 in the league's records comes from. */}
                    <td className="numeric strong">{row.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section>
          <h3>Still to play</h3>
          {remaining_schedule.length === 0 ? (
            <Empty>The season is complete.</Empty>
          ) : (
            <ul className="event-list">
              {remaining_schedule.map((event) => (
                <EventLine key={event.id} event={event} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </section>
  )
}
